import React from 'react';
import { RecentEntryWithGestures } from './RecentEntryWithGestures';
import { MADE_STATS, MISS_STATS, SCORING_VALUES, STAT_LABEL } from './gameConstants';
import { computeRunningTotals, formatClock, teamKeyOf } from './gameUtils';

// "2pts 1-1 fg" hint under scoring plays.
const scoringFooter = (entry, boxscore) => {
  const p = (boxscore?.[teamKeyOf(entry.team)] || []).find(x => x.player_id === entry.player_id);
  if (!p) return '';
  return `${p.pts || 0}pts ${p.fgm || 0}-${p.fga || 0} fg`;
};

function FeedEntry({ entry: e, player: p, runningTotal, boxscore, quarter }) {
  const made = MADE_STATS.includes(e.stat_type);
  const miss = MISS_STATS.includes(e.stat_type);
  const labelColor = made ? 'text-emerald-300' : miss ? 'text-rose-300' : 'text-amber-300';
  const initial = p.name?.split(' ')?.[0]?.[0] || '';
  const lastName = (p.name || '').split(' ').slice(1).join(' ');
  const hasClock = typeof e.game_clock_sec === 'number';
  return (
    <li className="text-center cursor-pointer" data-testid={`feed-${e.id}`}>
      <div className={`text-xs sm:text-sm font-bold ${labelColor}`}>
        {STAT_LABEL[e.stat_type] || e.stat_type}
        {runningTotal ? <span className="text-white/60 font-medium ml-1">({runningTotal})</span> : null}
      </div>
      <div className="text-white text-sm sm:text-base font-medium">
        #{p.jersey_number} {initial}. {lastName}
      </div>
      {SCORING_VALUES[e.stat_type] !== undefined && (
        <div className="text-white/60 text-[11px] sm:text-xs">{scoringFooter(e, boxscore)}</div>
      )}
      {(hasClock || e.quarter) && (
        <div className="text-white/30 text-[10px] sm:text-[11px] tracking-wider mt-0.5">
          Q{e.quarter || quarter}{hasClock ? ` · ${formatClock(e.game_clock_sec)}` : ''}
        </div>
      )}
    </li>
  );
}

function TapToEdit({ onEdit, testId, children }) {
  return (
    <div role="button" tabIndex={0} onClick={onEdit}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onEdit(); }
      }}
      className="rounded-lg hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400/40"
      data-testid={testId}>
      {children}
    </div>
  );
}

// Center column: last FEED_MAX plays; latest supports swipe/hold-to-undo, all support tap-to-edit.
export function PlayFeed({ recentEntries, boxscore, playersByTeam, quarter, onUndo, onEdit }) {
  const { feedEntries, totals } = computeRunningTotals(recentEntries, boxscore);
  const playerById = (id) => playersByTeam.a.find(x => x.id === id) || playersByTeam.b.find(x => x.id === id);
  return (
    <div className="col-span-6 sm:col-span-6 flex flex-col" data-testid="play-feed">
      <div className="flex-1 overflow-y-auto px-2">
        {feedEntries.length === 0 ? (
          <div className="h-full flex items-center justify-center text-white/30 text-center px-4">
            <div>
              <p className="text-sm">No plays yet.</p>
              <p className="text-xs mt-1">Tap a stat button to record.</p>
            </div>
          </div>
        ) : (
          <ul className="space-y-3 py-3">
            {feedEntries.map((e, idx) => {
              const p = playerById(e.player_id);
              if (!p) return null;
              const inner = <FeedEntry entry={e} player={p} runningTotal={totals.get(e.id)} boxscore={boxscore} quarter={quarter} />;
              if (idx === 0) {
                return (
                  <RecentEntryWithGestures key={e.id} onUndo={onUndo} onTap={() => onEdit(e)} testId={`feed-gesture-${e.id}`}>
                    {inner}
                  </RecentEntryWithGestures>
                );
              }
              return <TapToEdit key={e.id} onEdit={() => onEdit(e)} testId={`feed-tap-${e.id}`}>{inner}</TapToEdit>;
            })}
          </ul>
        )}
      </div>
      {recentEntries.length > 0 && (
        <div className="text-center text-[10px] uppercase tracking-widest text-white/30 pb-2 px-2">
          tip: tap any play to edit · swipe-left or hold the latest to undo
        </div>
      )}
    </div>
  );
}
